import React, { forwardRef } from 'react';
import './Input.css';

/**
 * Reusable Input Component
 *
 * @param {string} label - Input label
 * @param {string} error - Error message
 * @param {string} hint - Helper text
 * @param {React.ReactNode} leftIcon - Icon to show before input
 * @param {React.ReactNode} rightIcon - Icon to show after input
 * @param {string} className - Additional CSS classes
 */
const Input = forwardRef(({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  className = '',
  fullWidth = true,
  ...props
}, ref) => {
  const inputClasses = [
    'input-field',
    error && 'input-error',
    leftIcon && 'input-with-left-icon',
    rightIcon && 'input-with-right-icon',
    className
  ].filter(Boolean).join(' ');

  const wrapperClasses = [
    'input-wrapper',
    fullWidth && 'input-full-width'
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="input-label">
          {label}
          {props.required && <span className="input-required">*</span>}
        </label>
      )}
      <div className="input-container">
        {leftIcon && (
          <span className="input-icon input-icon-left">{leftIcon}</span>
        )}
        <input
          ref={ref}
          className={inputClasses}
          {...props}
        />
        {rightIcon && (
          <span className="input-icon input-icon-right">{rightIcon}</span>
        )}
      </div>
      {error && (
        <span className="input-error-text">{error}</span>
      )}
      {!error && hint && (
        <span className="input-hint">{hint}</span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
